/**
 * The verifier's inbox: what has been asked of this workspace, and answering it.
 */
import type {
  AnswerVerificationRequest,
  ReferVerificationRequest,
  VerificationRequestsResponse,
} from "@ondo/shared";

import { request } from "./request.ts";

/**
 * The verifier's inbox.
 *
 * `principal` scopes it to one workspace. A person who also belongs to a gallery
 * holds two, and a request addressed to the gallery does not belong in their
 * personal collector inbox — it was not addressed to them. Omitting it returns
 * everything the caller can reach, which is what a badge count wants.
 *
 * Not a filter to be trusted here: the server refuses a principal the caller
 * does not act as, so this narrows a set rather than choosing one.
 */
export const fetchVerificationRequests = (principal?: string | null) =>
  request<VerificationRequestsResponse>(
    principal
      ? `/verification-requests?principal=${encodeURIComponent(principal)}`
      : "/verification-requests",
  ).then((body) => body.requests);

/**
 * Answering, in one call for all four answers.
 *
 * Which table the answer lands in — an attestation for an entry-scoped request,
 * the request row itself for a document-scoped one — is the server's business.
 * A client that had to know would be a client that could get it wrong.
 */
export const answerVerificationRequest = (id: string, body: AnswerVerificationRequest) =>
  request<void>(`/verification-requests/${id}/answer`, body);

/** Not me, them. Ends the request and opens one in the referrer's name. */
export const referVerificationRequest = (id: string, body: ReferVerificationRequest) =>
  request<void>(`/verification-requests/${id}/refer`, body);
